import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';

import { Flame } from '@sorare/core/src/atoms/icons/Flame';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgressIndicator } from '@sorare/core/src/atoms/loader/CircularProgressIndicator';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { getRarityColor } from '@sorare/core/src/lib/colors';

import type { StreakProgressIndicator_thresholdsStreakTask } from './__generated__/index.graphql.ts';

type Props = {
  task: StreakProgressIndicator_thresholdsStreakTask;
  size?: 'sm' | 'md';
};

export const StreakProgressIndicator = ({ task, size = 'md' }: Props) => {
  const { formatNumber } = useIntl();

  const indicatorsSize = size === 'md' ? 8 : 6;
  const bottomIconSize = size === 'sm' ? 1.5 : 1;

  const liveScore = task.currentThresholdStatus.liveScore || 0;
  const reached = liveScore >= task.currentThreshold.score;
  const progress = Math.min(liveScore / task.currentThreshold.score, 1);

  return (
    <CircularProgressIndicator
      key={task.id}
      colors={
        reached
          ? [{ from: '#FF4E22', to: '#FEF314' }]
          : [{ from: getRarityColor(task.rarity) }]
      }
      text={formatNumber(task.currentThreshold.score, {
        maximumFractionDigits: 0,
      })}
      bottom={
        <Horizontal gap={0.5} center>
          <Flame size={bottomIconSize} color={getRarityColor(task.rarity)} />
          <BrandXXS>{task.progress + 1}</BrandXXS>
        </Horizontal>
      }
      shadow={reached ? 'rgba(255, 88, 34, 0.40)' : undefined}
      progresses={[progress]}
      size={indicatorsSize}
    />
  );
};

StreakProgressIndicator.fragments = {
  thresholdsStreakTask: gql`
    fragment StreakProgressIndicator_thresholdsStreakTask on ThresholdsStreakTask {
      id
      progress
      rarity
      currentThreshold {
        score
      }
      currentThresholdStatus {
        liveScore
      }
    }
  ` as TypedDocumentNode<StreakProgressIndicator_thresholdsStreakTask>,
};
